// src/components/dashboard/PrintableFinancialOverview.tsx
import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';

interface FinancialSummary {
  totalRegistrationFees: number;
  totalTShirtFees: number;
  totalCateringFees: number;
  totalOvernightFees: number;
  totalAmount: number;
  totalPaidAmount: number;
  totalOutstandingAmount: number;
  paymentsByStatus: {
    paid: { count: number; amount: number };
    partial: { count: number; amount: number };
    unpaid: { count: number; amount: number };
  };
}

interface ClubFinancialDetail {
  clubId: string;
  clubName: string;
  fees: {
    registrationFees: number;
    tShirtFees: number;
    cateringFees: number;
    overnightFees: number;
    totalAmount: number;
  };
  payment?: {
    amount: number;
    status: 'paid' | 'partial' | 'unpaid';
    receivedDate?: Date;
  };
  outstandingAmount: number;
}

interface PrintableFinancialOverviewProps {
  financialSummary: FinancialSummary;
  clubDetails: ClubFinancialDetail[];
  competitionName: string;
}

const PrintableFinancialOverview: React.FC<PrintableFinancialOverviewProps> = ({
  financialSummary,
  clubDetails,
  competitionName
}) => {
  const [open, setOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handlePrint = () => {
    if (printRef.current) {
      const printContent = printRef.current.innerHTML;
      const printWindow = window.open('', '_blank');
      
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Finanzübersicht - ${competitionName}</title>
              <meta charset="utf-8">
              <style>
                body {
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                  margin: 20px;
                  font-size: 12px;
                  line-height: 1.4;
                }
                .header {
                  text-align: center;
                  margin-bottom: 30px;
                  border-bottom: 2px solid #1976d2;
                  padding-bottom: 20px;
                }
                .header h1 {
                  color: #1976d2;
                  margin: 0;
                  font-size: 24px;
                }
                .header h2 {
                  color: #666;
                  margin: 5px 0 0 0;
                  font-size: 18px;
                  font-weight: normal;
                }
                .summary-section {
                  margin-bottom: 30px;
                }
                .summary-grid {
                  display: grid;
                  grid-template-columns: 1fr 1fr;
                  gap: 20px;
                  margin-bottom: 20px;
                }
                .summary-card {
                  border: 1px solid #ddd;
                  border-radius: 8px;
                  padding: 15px;
                  background-color: #f9f9f9;
                }
                .summary-card h3 {
                  margin: 0 0 10px 0;
                  color: #1976d2;
                  font-size: 14px;
                  border-bottom: 1px solid #ddd;
                  padding-bottom: 5px;
                }
                .summary-row {
                  display: flex;
                  justify-content: space-between;
                  margin: 5px 0;
                }
                .summary-row.total {
                  font-weight: bold;
                  border-top: 1px solid #ddd;
                  padding-top: 5px;
                  margin-top: 10px;
                }
                table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-top: 20px;
                  font-size: 11px;
                }
                th, td {
                  border: 1px solid #ddd;
                  padding: 8px;
                  text-align: left;
                }
                th {
                  background-color: #1976d2;
                  color: white;
                  font-weight: bold;
                  text-align: center;
                }
                td.number {
                  text-align: right;
                  font-family: monospace;
                }
                td.center {
                  text-align: center;
                }
                .status-paid { color: #2e7d32; font-weight: bold; }
                .status-partial { color: #f57c00; font-weight: bold; }
                .status-unpaid { color: #d32f2f; font-weight: bold; }
                .status-none { color: #666; font-style: italic; }
                .footer {
                  margin-top: 30px;
                  text-align: center;
                  font-size: 10px;
                  color: #666;
                  border-top: 1px solid #ddd;
                  padding-top: 10px;
                }
                @media print {
                  body { margin: 15px; }
                  .summary-grid { break-inside: avoid; }
                  table { break-inside: auto; }
                  tr { break-inside: avoid; break-after: auto; }
                  thead { display: table-header-group; }
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }
  };

  const formatCurrency = (amount: number) => `${amount.toFixed(2)} €`;

  // Hilfsfunktion: Korrigiere Zahlungsstatus
  const getCorrectPaymentStatus = (club: ClubFinancialDetail): 'paid' | 'partial' | 'unpaid' => {
    if (!club.payment) return 'unpaid';
    
    const paidAmount = Number((club.payment.amount || 0).toFixed(2));
    const expectedAmount = Number(club.fees.totalAmount.toFixed(2));
    
    if (paidAmount === expectedAmount) {
      return 'paid';
    } else if (paidAmount > 0) {
      return 'partial';
    } else {
      return 'unpaid';
    }
  };

  const getPaymentStatusLabel = (club: ClubFinancialDetail) => {
    const correctedStatus = getCorrectPaymentStatus(club);
  
    switch (correctedStatus) {
      case 'paid': return 'Bezahlt';
      case 'partial': return 'Teilweise';
      case 'unpaid': return club.payment ? 'Offen' : 'Keine Zahlung';
      default: return 'Keine Zahlung';
    }
  };

  const getPaymentStatusClass = (club: ClubFinancialDetail) => {
    const correctedStatus = getCorrectPaymentStatus(club);
    
    switch (correctedStatus) {
      case 'paid': return 'status-paid';
      case 'partial': return 'status-partial';
      case 'unpaid': return 'status-unpaid';
      default: return 'status-none';
    }
  };

  const calculatePaymentProgress = () => {
    if (financialSummary.totalAmount === 0) return 0;
    return (financialSummary.totalPaidAmount / financialSummary.totalAmount) * 100;
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<PrintIcon />}
        onClick={handleOpen}
        sx={{ mr: 1 }}
      >
        Finanzübersicht drucken
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Druckvorschau - Finanzübersicht</Typography>
          <Button onClick={handleClose} color="inherit">
            <CloseIcon />
          </Button>
        </DialogTitle>
        
        <DialogContent>
          <div ref={printRef}>
            {/* Header */}
            <div className="header">
              <h1>Finanzübersicht</h1>
              <h2>{competitionName}</h2>
              <p>Erstellt am {dayjs().format('DD.MM.YYYY HH:mm')} Uhr</p>
            </div>

            {/* Zusammenfassung */}
            <div className="summary-section">
              <div className="summary-grid">
                {/* Gebühren-Aufschlüsselung */}
                <div className="summary-card">
                  <h3>Gebühren-Aufschlüsselung</h3>
                  <div className="summary-row">
                    <span>Meldegebühren:</span>
                    <span>{formatCurrency(financialSummary.totalRegistrationFees)}</span>
                  </div>
                  <div className="summary-row">
                    <span>T-Shirt Gebühren:</span>
                    <span>{formatCurrency(financialSummary.totalTShirtFees)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Verpflegung:</span>
                    <span>{formatCurrency(financialSummary.totalCateringFees)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Übernachtung:</span>
                    <span>{formatCurrency(financialSummary.totalOvernightFees)}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Gesamtsumme:</span>
                    <span>{formatCurrency(financialSummary.totalAmount)}</span>
                  </div>
                </div>

                {/* Zahlungsübersicht */}
                <div className="summary-card">
                  <h3>Zahlungsübersicht</h3>
                  <div className="summary-row">
                    <span>Eingegangen:</span>
                    <span className="status-paid">{formatCurrency(financialSummary.totalPaidAmount)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Ausstehend:</span>
                    <span className="status-unpaid">{formatCurrency(financialSummary.totalOutstandingAmount)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Zahlungsquote:</span>
                    <span>{calculatePaymentProgress().toFixed(1)}%</span>
                  </div>
                  <div className="summary-row total">
                    <span>Vereine gesamt:</span>
                    <span>{clubDetails.length}</span>
                  </div>
                </div>
              </div>

              {/* Zahlungsstatus-Übersicht */}
              <div className="summary-card">
                <h3>Zahlungsstatus-Verteilung</h3>
                <div className="summary-row">
                  <span>Vollständig bezahlt:</span>
                  <span>
                    <span className="status-paid">{financialSummary.paymentsByStatus.paid.count} Vereine</span>
                    {' • '}
                    <span>{formatCurrency(financialSummary.paymentsByStatus.paid.amount)}</span>
                  </span>
                </div>
                <div className="summary-row">
                  <span>Teilweise bezahlt:</span>
                  <span>
                    <span className="status-partial">{financialSummary.paymentsByStatus.partial.count} Vereine</span>
                    {' • '}
                    <span>{formatCurrency(financialSummary.paymentsByStatus.partial.amount)}</span>
                  </span>
                </div>
                <div className="summary-row">
                  <span>Unbezahlt:</span>
                  <span>
                    <span className="status-unpaid">{financialSummary.paymentsByStatus.unpaid.count} Vereine</span>
                    {' • '}
                    <span>{formatCurrency(financialSummary.paymentsByStatus.unpaid.amount)}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Detaillierte Vereinstabelle */}
            <h3 style={{ color: '#1976d2', marginBottom: '10px' }}>Detaillierte Vereinsübersicht</h3>
            <table>
              <thead>
                <tr>
                  <th>Verein</th>
                  <th>Meldegebühren</th>
                  <th>T-Shirts</th>
                  <th>Verpflegung</th>
                  <th>Übernachtung</th>
                  <th>Gesamt</th>
                  <th>Zahlungsstatus</th>
                  <th>Eingegangen</th>
                  <th>Eingangsdatum</th>
                  <th>Offen</th>
                </tr>
              </thead>
              <tbody>
                {clubDetails.map((club) => (
                  <tr key={club.clubId}>
                    <td>{club.clubName}</td>
                    <td className="number">{formatCurrency(club.fees.registrationFees)}</td>
                    <td className="number">{formatCurrency(club.fees.tShirtFees)}</td>
                    <td className="number">{formatCurrency(club.fees.cateringFees)}</td>
                    <td className="number">{formatCurrency(club.fees.overnightFees)}</td>
                    <td className="number" style={{ fontWeight: 'bold' }}>
                      {formatCurrency(club.fees.totalAmount)}
                    </td>
                    <td className={`center ${getPaymentStatusClass(club)}`}>
                      {getPaymentStatusLabel(club)}
                    </td>
                    <td className="number">
                      {club.payment ? formatCurrency(club.payment.amount) : formatCurrency(0)}
                    </td>
                    <td className="center">
                      {club.payment?.receivedDate 
                        ? dayjs(club.payment.receivedDate).format('DD.MM.YYYY')
                        : '-'
                      }
                    </td>
                    <td className={`number ${club.outstandingAmount > 0 ? 'status-unpaid' : 'status-paid'}`}
                        style={{ fontWeight: 'bold' }}>
                      {formatCurrency(club.outstandingAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Footer */}
            <div className="footer">
              <p>Erstellt am {dayjs().format('DD.MM.YYYY HH:mm')} Uhr • {clubDetails.length} Vereine • Gesamtsumme: {formatCurrency(financialSummary.totalAmount)}</p>
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="inherit">
            Schließen
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            startIcon={<PrintIcon />}
            color="primary"
          >
            Drucken
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrintableFinancialOverview;